import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import ProfilePictureUpload from '../../components/BridgeClub/ProfilePictureUpload';
import WelcomePopup from '../../components/BridgeClub/WelcomePopup';
import RecoverPurchasesModal from '../../components/BridgeClub/RecoverPurchasesModal';
import LocalPointsCard from '../../components/BridgeClub/LocalPointsCard';
import TaxExemptionSection from '../../components/BridgeClub/TaxExemptionSection';
import MemberAccountSection from '../../components/BridgeClub/MemberAccountSection';
import DeliveryAddressSection from './DeliveryAddressSection';
import DeliveryAccessSection from '../../components/BridgeClub/DeliveryAccessSection';
import { useBridgeClubAuth } from '../../contexts/BridgeClubAuthContext';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useHelcimAddCard } from '../../components/BridgeClub/payments/useHelcimAddCard';
import './BridgeClubProfile.css';

interface LinkedStore {
  id: number;
  store_id: number;
  store_name: string;
  partner_member_id: string;
  linked_at: string;
  linked_via: string;
}

interface AvailableStore {
  id: number;
  name: string;
  city: string;
  state: string;
}

const BridgeClubProfile: React.FC = () => {
  const { member, refreshMember, logout } = useBridgeClubAuth();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(
    member?.profile_picture_url || null
  );
  const [showTutorial, setShowTutorial] = useState(false);

  // Linked Store Accounts state
  const [linkedStores, setLinkedStores] = useState<LinkedStore[]>([]);
  const [availableStores, setAvailableStores] = useState<AvailableStore[]>([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [loyaltyNumber, setLoyaltyNumber] = useState('');
  const [linkingStore, setLinkingStore] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<number | null>(null);

  // Payment Methods state (HelcimPay verify → vault)
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [removingCardId, setRemovingCardId] = useState<number | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<number | null>(null);
  // Which card is awaiting a "Remove ...?" confirmation, and which just became
  // default (for inline, at-the-control confirmation).
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<number | null>(null);
  const [justDefaultedId, setJustDefaultedId] = useState<number | null>(null);

  // Recover Past Purchases — persistent profile-page link. Visible
  // when state is NEITHER 'completed' NOR 'not_applicable'. We learn
  // the state lazily by hitting the prompt endpoint on mount.
  const [recoverPromptState, setRecoverPromptState] = useState<{
    should_show: boolean;
    reason: 'first_login' | 'profile_link' | 'not_applicable';
    state: 'not_shown' | 'not_applicable' | 'dismissed' | 'completed';
    has_existing_history: boolean;
  } | null>(null);
  const [showRecoverModal, setShowRecoverModal] = useState(false);
  const recoverLinkVisible =
    recoverPromptState !== null
    && recoverPromptState.state !== 'not_applicable'
    && recoverPromptState.state !== 'completed';

  // Get member initials for placeholder
  const memberInitials = `${member?.first_name?.[0] || ''}${member?.last_name?.[0] || ''}`.toUpperCase();

  const [formData, setFormData] = useState({
    first_name: member?.first_name || '',
    last_name: member?.last_name || '',
    phone: member?.phone || '',
    city: member?.city || '',
    state: member?.state || '',
    zip_code: member?.zip_code || '',
    feed_reminders_enabled: member?.feed_reminders_enabled ?? true,
    weather_alerts_enabled: member?.weather_alerts_enabled ?? true,
    reminder_days_before: member?.reminder_days_before ?? 7,
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await bridgeClubAPI.updateProfile(formData);
      await refreshMember();
      setSuccess('Profile updated successfully!');
      setEditing(false);
    } catch (err: any) {
      setError(err?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/bridge-club/login');
  };

  // Load linked stores on mount
  useEffect(() => {
    const loadLinkedStores = async () => {
      try {
        const data = await bridgeClubAPI.getLinkedStores();
        setLinkedStores(data);
      } catch (err) {
        console.error('Failed to load linked stores:', err);
      }
    };
    loadLinkedStores();
  }, []);

  // Load recover-prompt state on mount so we know whether to show
  // the persistent profile-page link. Lazy fix-up runs on the backend
  // for first-time non-local hits, so this also handles the auto-init
  // for existing members on first visit.
  useEffect(() => {
    const loadRecoverState = async () => {
      try {
        const data = await bridgeClubAPI.getRecoverPurchasesPrompt();
        setRecoverPromptState(data);
      } catch (err) {
        console.error('Failed to load recover-purchases state:', err);
      }
    };
    loadRecoverState();
  }, []);

  // After the modal completes (any outcome), backend flipped state to
  // 'completed'; re-fetch so the link disappears immediately.
  const handleRecoverCompleted = async () => {
    try {
      const data = await bridgeClubAPI.getRecoverPurchasesPrompt();
      setRecoverPromptState(data);
    } catch {
      // Best-effort — link stays as-is on error.
    }
  };

  // Load available stores when modal opens
  useEffect(() => {
    if (showLinkModal) {
      const loadAvailableStores = async () => {
        try {
          const data = await bridgeClubAPI.getAvailableStores();
          setAvailableStores(data);
        } catch (err) {
          console.error('Failed to load available stores:', err);
        }
      };
      loadAvailableStores();
    }
  }, [showLinkModal]);

  const handleLinkStore = async () => {
    if (!selectedStoreId || !loyaltyNumber.trim()) {
      setError('Please select a store and enter your loyalty number');
      return;
    }

    setLinkingStore(true);
    setError(null);

    try {
      const result = await bridgeClubAPI.linkPartnerStore(selectedStoreId, loyaltyNumber.trim());
      setLinkedStores([...linkedStores, {
        id: result.id,
        store_id: result.store_id,
        store_name: result.store_name,
        partner_member_id: result.partner_member_id,
        linked_at: result.linked_at,
        linked_via: 'customer_app',
      }]);
      setShowLinkModal(false);
      setSelectedStoreId(null);
      setLoyaltyNumber('');
      setSuccess('Store account linked successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.error || 'Failed to link store');
    } finally {
      setLinkingStore(false);
    }
  };

  const handleUnlinkStore = async (linkId: number) => {
    setUnlinkingId(linkId);
    try {
      await bridgeClubAPI.unlinkStore(linkId);
      setLinkedStores(linkedStores.filter(s => s.id !== linkId));
      setSuccess('Store account unlinked');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.error || 'Failed to unlink store');
    } finally {
      setUnlinkingId(null);
    }
  };

  const getSelectedStoreName = () => {
    const store = availableStores.find(s => s.id === selectedStoreId);
    return store?.name || 'the store';
  };

  // ── Payment Methods ────────────────────────────────────────────────
  const loadPaymentMethods = async () => {
    try {
      const data = await bridgeClubAPI.getSavedPaymentMethods();
      setPaymentMethods(data?.payment_methods || []);
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    }
  };

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  // The add-card HelcimPay flow now lives in a shared hook (also used by the onboarding
  // card step). This page keeps ownership of what happens after: refresh the list and
  // toast on success, surface the message on error.
  const { startAddCard, adding: addingCard } = useHelcimAddCard({
    onAdded: async () => {
      await loadPaymentMethods();
      setSuccess('Card added.');
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (m) => setError(m || null),
  });

  // Only called after the inline "Remove ...?" confirmation is accepted.
  const handleRemoveCard = async (methodId: number) => {
    setRemovingCardId(methodId);
    try {
      const data = await bridgeClubAPI.deleteSavedPaymentMethod(methodId);
      setPaymentMethods(data?.payment_methods || []);
      setSuccess('Card removed.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.error || 'Failed to remove card');
    } finally {
      setRemovingCardId(null);
      setConfirmingRemoveId(null);
    }
  };

  const handleSetDefault = async (methodId: number) => {
    setSettingDefaultId(methodId);
    try {
      const data = await bridgeClubAPI.setDefaultPaymentMethod(methodId);
      setPaymentMethods(data?.payment_methods || []);
      // Confirm inline on the row — the top-of-page banner is off-screen here.
      setJustDefaultedId(methodId);
      setTimeout(
        () => setJustDefaultedId(prev => (prev === methodId ? null : prev)),
        3000,
      );
    } catch (err: any) {
      setError(err?.error || 'Failed to set default card');
    } finally {
      setSettingDefaultId(null);
    }
  };

  if (!member) {
    return null;
  }

  return (
    <BridgeClubLayout>
      <div className="profile-page">
        <h1>My Profile</h1>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Profile Card */}
        <div className="profile-card">
          <div className="profile-header">
            <ProfilePictureUpload
              currentImageUrl={profilePictureUrl}
              memberInitials={memberInitials}
              onUploadSuccess={(url) => {
                setProfilePictureUrl(url);
                setSuccess('Profile picture updated!');
                setTimeout(() => setSuccess(null), 3000);
              }}
              onDeleteSuccess={() => {
                setProfilePictureUrl(null);
                setSuccess('Profile picture removed.');
                setTimeout(() => setSuccess(null), 3000);
              }}
            />
            <div className="profile-info">
              <span className="profile-name">{member.full_name}</span>
              <span className="profile-email">{member.email}</span>
            </div>
          </div>

          <div className="profile-details">
            <div className="detail-row">
              <span className="detail-label">Member Code</span>
              <span className="detail-value code">{member.member_code}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Member Since</span>
              <span className="detail-value">
                {new Date(member.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Home Store</span>
              <span className="detail-value">
                {member.linked_store?.name || 'Not linked'}
              </span>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        {editing ? (
          <form onSubmit={handleSave} className="edit-form">
            <div className="form-section">
              <h3>Personal Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">First Name</label>
                  <input
                    id="first_name"
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="last_name">Last Name</label>
                  <input
                    id="last_name"
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            {member.location_source === 'delivery' ? (
            <div className="form-section">
              <h3>Location</h3>
              <p className="toggle-desc">
                Your location comes from your delivery address. Change it under Delivery address.
              </p>
            </div>
            ) : (
            <div className="form-section">
              <h3>Location</h3>
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input
                    id="state"
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="zip_code">ZIP Code</label>
                  <input
                    id="zip_code"
                    type="text"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  />
                </div>
              </div>
            </div>
            )}

            <div className="form-section">
              <h3>Notifications</h3>
              <div className="toggle-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.feed_reminders_enabled}
                    onChange={(e) => setFormData({ ...formData, feed_reminders_enabled: e.target.checked })}
                  />
                  <span>Feed Reminders</span>
                </label>
                <p className="toggle-desc">Get notified when your feed is running low</p>
              </div>
              <div className="toggle-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.weather_alerts_enabled}
                    onChange={(e) => setFormData({ ...formData, weather_alerts_enabled: e.target.checked })}
                  />
                  <span>Weather Alerts</span>
                </label>
                <p className="toggle-desc">Receive weather alerts with animal care tips</p>
              </div>
              {formData.feed_reminders_enabled && (
                <div className="form-group">
                  <label htmlFor="reminder_days">Remind me before feed runs out</label>
                  <select
                    id="reminder_days"
                    value={formData.reminder_days_before}
                    onChange={(e) => setFormData({ ...formData, reminder_days_before: parseInt(e.target.value) })}
                  >
                    <option value={3}>3 days before</option>
                    <option value={5}>5 days before</option>
                    <option value={7}>7 days before</option>
                    <option value={10}>10 days before</option>
                    <option value={14}>14 days before</option>
                  </select>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" onClick={() => setEditing(false)} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <button onClick={() => setEditing(true)} className="edit-btn">
            Edit Profile
          </button>
        )}

        {member.location_source === 'delivery' && (
          <>
            <DeliveryAddressSection onChanged={refreshMember} />
            {/* The onboarding answers, editable, with the photos (Brad, 2026-09-23). */}
            <DeliveryAccessSection />
          </>
        )}

        {/* Loyalty — local (Kettle Falls) members are on the POINTS program
            ($1=1pt, 500pts=$10); retail-partner members keep the bags punch
            card. Gate on local_access so each member sees THEIR program. */}
        {member.local_access ? (
          <>
            <LocalPointsCard />
            {/* My Account — store credit, bag credit, tab + pay-tab (Phase 5). */}
            <MemberAccountSection />
            {/* Tax Exemption — local members only. */}
            <TaxExemptionSection />
          </>
        ) : (
          <div className="stats-card">
            <h3>Loyalty Stats</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-value">{member.loyalty_bags_purchased}</span>
                <span className="stat-label">Total Bags</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{member.loyalty_progress}/10</span>
                <span className="stat-label">Current Progress</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{member.loyalty_bags_redeemed}</span>
                <span className="stat-label">Rewards Used</span>
              </div>
            </div>
          </div>
        )}

        {/* Payment Methods */}
        <div className="linked-stores-section">
          <h3>Payment Methods</h3>
          <p className="section-desc">Save a card for faster local checkout. Card details are entered securely with Helcim — we never see or store your card number.</p>

          {paymentMethods.length > 0 ? (
            <div className="linked-stores-list">
              {/* Stable order by creation (newest first), independent of the
                  server's -is_default sort — the list must NOT move when the
                  default changes, or the next click lands on the wrong row. */}
              {[...paymentMethods]
                .sort((a, b) =>
                  (new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  || (b.id - a.id))
                .map((m) => (
                <div key={m.id} className="linked-store-item">
                  <div className="store-info">
                    <span className="store-icon">💳</span>
                    <div className="store-details">
                      <span className="store-name">
                        {(m.card_type || 'Card')} •••• {m.last_four || '????'}
                        {m.is_default && (
                          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#065F46' }}>Default</span>
                        )}
                      </span>
                      {m.card_expiry && (
                        <span className="store-linked-date">Exp {m.card_expiry}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {confirmingRemoveId === m.id ? (
                      // Destructive + irreversible (drops the Helcim vault card) —
                      // name the card and require an explicit confirm.
                      <>
                        <span style={{ fontSize: 12 }}>
                          Remove {(m.card_type || 'Card')} ••••{m.last_four || '????'}?
                        </span>
                        <button
                          className="unlink-btn"
                          onClick={() => handleRemoveCard(m.id)}
                          disabled={removingCardId === m.id}
                        >
                          {removingCardId === m.id ? 'Removing…' : 'Yes, remove'}
                        </button>
                        <button
                          className="link-store-btn"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => setConfirmingRemoveId(null)}
                          disabled={removingCardId === m.id}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        {justDefaultedId === m.id && (
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#065F46' }}>
                            ✓ Now default
                          </span>
                        )}
                        {!m.is_default && (
                          <button
                            className="link-store-btn"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            onClick={() => handleSetDefault(m.id)}
                            disabled={settingDefaultId === m.id}
                          >
                            {settingDefaultId === m.id ? 'Setting…' : 'Set default'}
                          </button>
                        )}
                        <button
                          className="unlink-btn"
                          onClick={() => setConfirmingRemoveId(m.id)}
                          disabled={settingDefaultId === m.id}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-linked-stores">No saved cards yet.</p>
          )}

          <button
            className="link-store-btn"
            onClick={() => { setError(null); startAddCard(); }}
            disabled={addingCard}
          >
            {addingCard ? 'Starting…' : '+ Add a Card'}
          </button>
        </div>

        {/* Linked Store Accounts */}
        <div className="linked-stores-section">
          <h3>Linked Store Accounts</h3>
          <p className="section-desc">Link your loyalty accounts from partner stores to earn rewards automatically.</p>

          {linkedStores.length > 0 ? (
            <div className="linked-stores-list">
              {linkedStores.map((store) => (
                <div key={store.id} className="linked-store-item">
                  <div className="store-info">
                    <span className="store-icon">🏪</span>
                    <div className="store-details">
                      <span className="store-name">{store.store_name}</span>
                      <span className="store-loyalty-id">{store.partner_member_id}</span>
                      <span className="store-linked-date">
                        Linked {new Date(store.linked_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                  <button
                    className="unlink-btn"
                    onClick={() => handleUnlinkStore(store.id)}
                    disabled={unlinkingId === store.id}
                  >
                    {unlinkingId === store.id ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-linked-stores">No store accounts linked yet.</p>
          )}

          <button
            className="link-store-btn"
            onClick={() => setShowLinkModal(true)}
          >
            + Link a Store Account
          </button>
        </div>

        {/* Link Store Modal */}
        {showLinkModal && (
          <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Link a Store Account</h3>
              <p>Connect your loyalty card from a partner store to earn rewards on purchases.</p>

              <div className="form-group">
                <label htmlFor="store-select">Select Store</label>
                <select
                  id="store-select"
                  value={selectedStoreId || ''}
                  onChange={(e) => setSelectedStoreId(Number(e.target.value) || null)}
                >
                  <option value="">Choose a store...</option>
                  {availableStores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name} - {store.city}, {store.state}
                    </option>
                  ))}
                </select>
              </div>

              {selectedStoreId && (
                <div className="form-group">
                  <label htmlFor="loyalty-number">
                    Your {getSelectedStoreName()} Loyalty Number
                  </label>
                  <input
                    id="loyalty-number"
                    type="text"
                    value={loyaltyNumber}
                    onChange={(e) => setLoyaltyNumber(e.target.value)}
                    placeholder="e.g., MON-45678"
                  />
                </div>
              )}

              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowLinkModal(false);
                    setSelectedStoreId(null);
                    setLoyaltyNumber('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleLinkStore}
                  disabled={linkingStore || !selectedStoreId || !loyaltyNumber.trim()}
                >
                  {linkingStore ? 'Linking...' : 'Link Account'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help & Tutorial Section */}
        <div className="help-section">
          <h3>Help & Tutorial</h3>
          <button onClick={() => setShowTutorial(true)} className="tutorial-btn">
            <span className="tutorial-icon">📺</span>
            <span className="tutorial-text">Watch Tutorial Video</span>
            <span className="tutorial-arrow">→</span>
          </button>
        </div>

        {/* Legal Section */}
        <div className="legal-section">
          <h3>Legal</h3>
          <Link to="/bridge-club/terms" className="legal-link-row">
            <span className="legal-icon">📄</span>
            <span className="legal-text">Terms of Service</span>
            <span className="legal-arrow">→</span>
          </Link>
          <Link to="/bridge-club/privacy" className="legal-link-row">
            <span className="legal-icon">🔒</span>
            <span className="legal-text">Privacy Policy</span>
            <span className="legal-arrow">→</span>
          </Link>
          {member.terms_accepted_at && (
            <p className="legal-agreed-date">
              You agreed on {new Date(member.terms_accepted_at).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="profile-actions">
          {recoverLinkVisible && (
            <button
              onClick={() => setShowRecoverModal(true)}
              className="action-btn"
              style={{
                background: '#ECFDF5',
                color: '#065F46',
                border: '1px solid #10B981',
              }}
            >
              🛒 Recover Past Purchases
            </button>
          )}
          <button onClick={() => navigate('/bridge-club/find-store')} className="action-btn">
            Change Home Store
          </button>
          <button onClick={handleLogout} className="action-btn logout">
            Log Out
          </button>
        </div>
      </div>

      {/* Tutorial Popup */}
      <WelcomePopup isOpen={showTutorial} onClose={() => setShowTutorial(false)} />

      {/* Recover Past Purchases modal — opened by the action button
          above. Same component as the dashboard banner uses. */}
      <RecoverPurchasesModal
        isOpen={showRecoverModal}
        onClose={() => setShowRecoverModal(false)}
        onCompleted={handleRecoverCompleted}
      />
    </BridgeClubLayout>
  );
};

export default BridgeClubProfile;
